import { defineStore } from 'pinia';
import { letterSocketBase } from '@/app.config.js';
import { parseLangText } from '@/utils/utils.js';
import { MessageLetterUnreadCount, MessageLetterRead } from '@/api/message.js';

// 模块级连接状态（不进 state，避免无谓的响应式开销）
let ws = null;
let heartbeatTimer = null;
let reconnectTimer = null;
let retry = 0;
let manualClosed = false;
let currentSaasId = '';
const seenIds = new Set(); // 弹窗去重（多标签页会各收到一份）

const HEARTBEAT_INTERVAL = 30000; // < 50s，避免网关空闲超时
const MAX_RECONNECT_DELAY = 30000;

export const useLetterStore = defineStore('letter', {
	state: () => ({
		unreadCount: 0,
		pendingPopup: null, // { id, title, content, sendUser }
		connected: false
	}),
	actions: {
		// 登录后建立连接；可重复调用（已连同账号时跳过，切号时自动重连）
		connect(saasId) {
			// #ifdef H5
			if (!saasId || !letterSocketBase) return;
			const sid = String(saasId);
			if (sid === currentSaasId && ws) return; // 已连同一账号
			if (ws) this.disconnect(); // 切号：先断开旧连接
			currentSaasId = sid;
			manualClosed = false;
			this._open();
			// #endif
		},

		// #ifdef H5
		_open() {
			this._clearTimers();
			try {
				ws = new WebSocket(`${letterSocketBase}/ws?saasId=${encodeURIComponent(currentSaasId)}`);
			} catch (e) {
				console.log('站内信 WS 创建失败', e);
				this._scheduleReconnect();
				return;
			}

			ws.onopen = () => {
				console.log('站内信 WS 连接成功');
				retry = 0;
				this.connected = true;
				heartbeatTimer = setInterval(() => {
					if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
				}, HEARTBEAT_INTERVAL);
			};

			ws.onmessage = (e) => {
				let msg;
				try {
					msg = JSON.parse(e.data);
				} catch (_) {
					return; // 6 位确认码 / 心跳回显等非 JSON，忽略
				}
				if (!msg || msg.event !== 'LETTER') return; // 只认站内信
				this._onLetter(msg);
			};

			ws.onclose = () => {
				this.connected = false;
				this._clearTimers();
				if (!manualClosed) this._scheduleReconnect();
			};

			ws.onerror = () => {
				if (ws) ws.close();
			};
		},

		_onLetter(msg) {
			const id = msg.messageId;
			if (id != null && seenIds.has(id)) return; // 去重
			if (id != null) seenIds.add(id);
			this.pendingPopup = {
				id,
				title: parseLangText(msg.title),
				content: parseLangText(msg.content),
				sendUser: msg.sendUser || ''
			};
			this.unreadCount += 1;
		},

		_scheduleReconnect() {
			const delay = Math.min(1000 * 2 ** retry++, MAX_RECONNECT_DELAY);
			reconnectTimer = setTimeout(() => this._open(), delay);
		},

		_clearTimers() {
			clearInterval(heartbeatTimer);
			clearTimeout(reconnectTimer);
			heartbeatTimer = null;
			reconnectTimer = null;
		},
		// #endif

		// 退出登录 / 切号时调用
		disconnect() {
			// #ifdef H5
			manualClosed = true;
			this._clearTimers();
			retry = 0;
			if (ws) {
				ws.onclose = null; // 主动关闭不触发重连
				ws.onerror = null;
				try { ws.close(); } catch (_) {}
				ws = null;
			}
			this.connected = false;
			this.pendingPopup = null;
			this.unreadCount = 0;
			seenIds.clear();
			// #endif
		},

		closePopup() {
			this.pendingPopup = null;
		},

		// 进入页面 / 兜底刷新未读数（REST，全平台可用）
		refreshUnread() {
			if (!uni.getStorageSync('token')) {
				this.unreadCount = 0;
				return Promise.resolve(0);
			}
			return MessageLetterUnreadCount().then(res => {
				this.unreadCount = Number(res ?? 0);
				return this.unreadCount;
			}).catch(() => this.unreadCount);
		},

		// 标记已读并同步未读数
		markRead(ids) {
			const list = [].concat(ids || []).filter(v => v != null);
			if (!list.length) return Promise.resolve();
			return MessageLetterRead({ ids: list }).then(res => {
				this.unreadCount = Math.max(0, this.unreadCount - list.length);
				return res;
			});
		},

		markAllRead() {
			return MessageLetterRead({ all: true }).then(res => {
				this.unreadCount = 0;
				return res;
			});
		}
	}
});
